import { useEffect, useRef, useState, useCallback } from 'react'

// Minimal typings for the Web Speech API (not in TS lib.dom). We only declare
// what we touch. Chrome exposes it as `webkitSpeechRecognition`.
type SpeechRecognitionAlternativeLike = { transcript: string; confidence: number }
type SpeechRecognitionResultLike = { isFinal: boolean; 0: SpeechRecognitionAlternativeLike; length: number }
type SpeechRecognitionEventLike = { resultIndex: number; results: { length: number; [i: number]: SpeechRecognitionResultLike } }
type SpeechRecognitionLike = {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((e: SpeechRecognitionEventLike) => void) | null
  onerror: ((e: any) => void) | null
  onend: (() => void) | null
  onspeechstart: (() => void) | null
  onspeechend: (() => void) | null
}

const getRecognitionCtor = (): (new () => SpeechRecognitionLike) | null => {
  const w = window as any
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

export type TranscriptEntry = {
  ts: number // ms since session epoch (passed in via tsBase)
  text: string
  isFinal: boolean
  confidence: number
}

export type UseSpeechOptions = {
  active: boolean // start/stop the recogniser
  lang?: string
  tsBase: number // performance-now origin so transcript ts are session-relative
  onTranscript: (entry: TranscriptEntry) => void
  onSpeechStart?: () => void
  onSpeechEnd?: () => void
  onPermissionDenied?: () => void
}

export type SpeechState = {
  supported: boolean
  listening: boolean
  error: string | null
}

// Wraps Web Speech with continuous + interim results. Chrome silently ends a
// continuous session every ~minute and on network blips; we auto-restart while
// `active` so the stream is unbroken from the player's point of view.
export const useSpeechRecognition = (opts: UseSpeechOptions): SpeechState => {
  const { active, lang = 'en-US', tsBase, onTranscript, onSpeechStart, onSpeechEnd, onPermissionDenied } = opts

  const supported = Boolean(getRecognitionCtor())
  const [listening, setListening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const recRef = useRef<SpeechRecognitionLike | null>(null)
  const activeRef = useRef(active)
  const wantRunningRef = useRef(false)

  // Keep latest callbacks without re-creating the recogniser.
  const cbRef = useRef({ onTranscript, onSpeechStart, onSpeechEnd, onPermissionDenied, tsBase })
  cbRef.current = { onTranscript, onSpeechStart, onSpeechEnd, onPermissionDenied, tsBase }

  const startRec = useCallback(() => {
    const Ctor = getRecognitionCtor()
    if (!Ctor) return
    if (recRef.current) return // already constructed & running
    const rec = new Ctor()
    rec.continuous = true
    rec.interimResults = true
    rec.lang = lang
    rec.maxAlternatives = 1

    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i]
        const alt = result[0]
        cbRef.current.onTranscript({
          ts: Math.round(performance.now() - cbRef.current.tsBase),
          text: alt.transcript,
          isFinal: result.isFinal,
          confidence: typeof alt.confidence === 'number' ? alt.confidence : 0,
        })
      }
    }
    rec.onspeechstart = () => cbRef.current.onSpeechStart?.()
    rec.onspeechend = () => cbRef.current.onSpeechEnd?.()
    rec.onerror = (ev: any) => {
      const err = ev?.error || 'unknown'
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        setError('microphone permission denied')
        wantRunningRef.current = false
        cbRef.current.onPermissionDenied?.()
      } else if (err === 'no-speech' || err === 'aborted') {
        // benign — onend will fire and we restart if still active
      } else {
        setError(err)
      }
    }
    rec.onend = () => {
      recRef.current = null
      setListening(false)
      // Auto-restart if the player is still meant to be talking.
      if (wantRunningRef.current && activeRef.current) {
        // small async hop avoids "recognition has already started" races
        setTimeout(() => {
          if (wantRunningRef.current && activeRef.current) startRec()
        }, 150)
      }
    }

    try {
      rec.start()
      recRef.current = rec
      setListening(true)
      setError(null)
    } catch {
      // start() throws if called too soon after a previous stop; onend retry covers it
      recRef.current = null
    }
  }, [lang])

  const stopRec = useCallback(() => {
    wantRunningRef.current = false
    const rec = recRef.current
    if (rec) {
      try {
        rec.stop()
      } catch {
        /* noop */
      }
    }
  }, [])

  useEffect(() => {
    activeRef.current = active
    if (!supported) return
    if (active) {
      wantRunningRef.current = true
      startRec()
    } else {
      stopRec()
    }
    return () => {
      stopRec()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, supported])

  return { supported, listening, error }
}
